import { useEffect, useState } from 'react';
import type { InviteInfo } from '@momomail/shared';
import { client, errorText, shell } from '../lib/bridge';
import { t } from '../lib/i18n';
import { formatTime, people, rememberUsername } from '../lib/people';
import { Icon } from './Icon';
import { Mascot } from './Mascot';

/**
 * Pages that don't require login (web app): #/forgot forgot password, #/reset/<token> set a new password, #/invite/<token> accept an invite.
 * The token is removed from the address bar as soon as it's read (history.replaceState), so it doesn't stay in browser history.
 */

export type PublicRoute = { kind: 'forgot' } | { kind: 'reset'; token: string } | { kind: 'invite'; token: string };

/** Read the address bar once on page load. The desktop shell doesn't have these pages (links open in the browser). */
export function takePublicRoute(): PublicRoute | null {
  if (shell) return null;
  const m = /^#\/(forgot|reset|invite)(?:\/([\w-]+))?\/?$/.exec(location.hash);
  if (!m) return null;
  const [, kind, token] = m;
  history.replaceState(null, '', location.pathname + location.search);
  if (kind === 'forgot') return { kind: 'forgot' };
  if (!token) return null;
  return kind === 'reset' ? { kind: 'reset', token } : { kind: 'invite', token };
}

function Card({ title, lead, children, open = false }: { title: string; lead?: React.ReactNode; children: React.ReactNode; open?: boolean }) {
  return (
    <main className="welcome login">
      <div className="welcome__card login__card">
        <div className="login__art" aria-hidden>
          <Mascot />
        </div>
        <h1 className="welcome__title">{title}</h1>
        {lead && <p className="welcome__lead">{lead}</p>}
        {children}
      </div>
    </main>
  );
}

function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="login__error" role="alert">
      <Icon name="alert" size={15} /> {error}
    </p>
  );
}

const BackToLogin = ({ onDone }: { onDone: () => void }) => (
  <button type="button" className="field__link" onClick={onDone}>
    <Icon name="back" size={13} /> {t('回到登录')}
  </button>
);

export function ForgotScreen({ onDone }: { onDone: () => void }) {
  const [login, setLogin] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await people.forgot(login.trim());
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return (
      <Card title={t('去邮箱看看')} lead={t('如果信息匹配，重置邮件已经发出，30 分钟内有效。没收到的话看看垃圾邮件，或者找管理员要一个重置链接。')}>
        <BackToLogin onDone={onDone} />
      </Card>
    );
  }
  return (
    <Card title={t('忘记密码')} lead={t('输入用户名或找回邮箱，我们会往你的找回邮箱发一个重置链接。')}>
      <form className="conn-form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">{t('用户名或找回邮箱')}</span>
          <input value={login} onChange={(e) => setLogin(e.target.value)} autoComplete="username" required autoFocus />
        </label>
        <ErrorLine error={error} />
        <div className="login__actions">
          <button className="btn btn--primary" type="submit" disabled={busy || !login.trim()}>
            {busy ? <span className="spinner" /> : <Icon name="mail" size={16} />} {t('发送重置邮件')}
          </button>
          <BackToLogin onDone={onDone} />
        </div>
      </form>
    </Card>
  );
}

/** New password ×2 (at least 8 characters, both must match). */
export function usePasswordPair() {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const problem = pw.length > 0 && pw.length < 8 ? t('密码至少 8 个字符') : pw2.length > 0 && pw !== pw2 ? t('两次输入的密码不一样') : null;
  const ready = pw.length >= 8 && pw === pw2;
  const fields = (label = t('新密码')) => (
    <>
      <label className="field">
        <span className="field__label">{t('{label}（至少 8 位）', { label })}</span>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" required />
      </label>
      <label className="field">
        <span className="field__label">{t('再输一次')}</span>
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" required />
      </label>
      {problem && <p className="muted">{problem}</p>}
    </>
  );
  return { pw, ready, fields, reset: () => (setPw(''), setPw2('')) };
}


export function ResetScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const pair = usePasswordPair();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await people.reset(token, pair.pw);
      setDone(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <Card title={t('密码改好了')} lead={t('用新密码登录吧。所有设备上原来的登录都已经退出了，手机 App 也要重新登录一次。')} open>
        <div className="login__actions">
          <button className="btn btn--primary" onClick={onDone}>
            <Icon name="lock" size={16} /> {t('去登录')}
          </button>
        </div>
      </Card>
    );
  }
  return (
    <Card title={t('设置新密码')} lead={t('这个链接只能用一次。设好以后，所有设备上原来的登录都会退出。')}>
      <form className="conn-form" onSubmit={(e) => void submit(e)}>
        {pair.fields()}
        <ErrorLine error={error} />
        <div className="login__actions">
          <button className="btn btn--primary" type="submit" disabled={busy || !pair.ready}>
            {busy ? <span className="spinner" /> : <Icon name="check" size={16} />} {t('设置新密码')}
          </button>
          <BackToLogin onDone={onDone} />
        </div>
      </form>
    </Card>
  );
}

export function InviteScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const pair = usePasswordPair();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    people.inviteInfo(token).then(setInfo, (e: unknown) => setLoadError(errorText(e)));
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const login = await people.acceptInvite(token, { username: username.trim(), password: pair.pw, recoveryEmail: email.trim() });
      rememberUsername(username);
      onDone();
      await client.useToken(login.token);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <Card title={t('打不开邀请')} lead={loadError}>
        <BackToLogin onDone={onDone} />
      </Card>
    );
  }
  if (!info) {
    return (
      <Card title={t('正在打开邀请…')}>
        <span className="spinner spinner--big" />
      </Card>
    );
  }
  if (!info.valid) {
    return (
      <Card title={t('这个邀请用不了')} lead={t('邀请链接无效、已经用过或者过期了，也可能服务器还没准备好接收新用户。请找管理员重新要一个。')}>
        <BackToLogin onDone={onDone} />
      </Card>
    );
  }
  const emailOk = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email.trim());
  return (
    <Card
      title={info.isAdmin ? t('加入馍馍邮箱（管理员）') : t('加入馍馍邮箱')}
      lead={
        <>
          {info.expiresAt ? t('设置你自己的用户名和密码，管理员看不到你的密码。邀请 {time} 前有效，只能用一次。', { time: formatTime(info.expiresAt) }) : t('设置你自己的用户名和密码，管理员看不到你的密码。邀请只能用一次。')}
        </>
      }
      open
    >
      <form className="conn-form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">{t('用户名')}</span>
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" placeholder={t('2–32 个字符：字母、数字、汉字、. _ -')} required autoFocus />
        </label>
        {pair.fields(t('密码'))}
        <label className="field">
          <span className="field__label">{t('找回邮箱')}</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="name@example.com" required />
          <span className="muted">{t('忘了密码时，重置链接会发到这里。请填一个你平时能收到信的邮箱。')}</span>
        </label>
        <ErrorLine error={error} />
        <div className="login__actions">
          <button className="btn btn--primary" type="submit" disabled={busy || !username.trim() || !pair.ready || !emailOk}>
            {busy ? <span className="spinner" /> : <Icon name="check" size={16} />} {t('创建账号并登录')}
          </button>
        </div>
      </form>
    </Card>
  );
}

export function PublicScreen({ route, onDone }: { route: PublicRoute; onDone: () => void }) {
  if (route.kind === 'forgot') return <ForgotScreen onDone={onDone} />;
  if (route.kind === 'reset') return <ResetScreen token={route.token} onDone={onDone} />;
  return <InviteScreen token={route.token} onDone={onDone} />;
}
